import React, { useState } from 'react';

import Markdown from 'markdown-to-jsx';
import Button, { LoadingButton } from '@atlaskit/button';
import Checkbox from '@atlaskit/checkbox';
import Heading from '@atlaskit/heading';
import Image from '@atlaskit/image';
import { Box, Stack, Text } from '@atlaskit/primitives';
import { useInterval } from '@atlassian/eoc-common';
import {
	srcDark as srcMovingDark,
	srcLight as srcMovingLight,
} from '@atlassian/jira-illustrations/src/ui/adg4/brand/heroes/components/moving';
import {
	srcDark as srcErrorDark,
	srcLight as srcErrorLight,
} from '@atlassian/jira-illustrations/src/ui/adg4/brand/spots/error/components/error';
import {
	srcDark as srcGenericErrorDark,
	srcLight as srcGenericErrorLight,
} from '@atlassian/jira-illustrations/src/ui/adg4/brand/spots/error/components/generic-error';
import { useIntl } from '@atlassian/jira-intl';
import { JIRA_SUPPORT_LINK } from '../../../common/constants';
import { Anchor } from '../../../common/ui/anchor';
import { TransitionHomeContentBox } from '../../../common/ui/transition-home/content-box';
import { TransitionHomeHeader } from '../../../common/ui/transition-home/header';
import { messages as stepMessages } from '../../../common/ui/transition-home/step/messages';
import {
	UnorderedList,
	UnorderedListItem,
} from '../../../common/ui/transition-home/unordered-list';
import taskMessages from '../../../common/utils/tasks/messages';
import { useIncidents } from '../../../services/store/incidents';
import TaskFourIllustration from './assets/incidents-task-four-illustration.png';
import TaskOneIllustrationDark from './assets/incidents-task-one-illustration-dark.png';
import TaskOneIllustration from './assets/incidents-task-one-illustration.png';
import TaskTwoIllustrationDark from './assets/incidents-task-two-illustration-dark.png';
import TaskTwoIllustration from './assets/incidents-task-two-illustration.png';

import { messages } from './messages';
import { PriorityTable } from './priority-table';
import { StatusTable } from './status-table';

export const IncidentsStep = () => {
	const { formatMessage } = useIntl();
	const [isChecked, setIsChecked] = useState(false);

	const [isCopyManuallyInitiated, setIsCopyManuallyInitiated] = useState(false);
	const [
		{
			projectKey,
			hasCopyOperationError,
			isCopied,
			isCopying,
			isEligibleForCopy,
			shouldDisplaySuccessBanner,
		},
		{ copyIncidents, getIncidentsCopyStatus },
	] = useIncidents();

	// If the copy operation is in progress, we need to keep checking the status in the interval
	useInterval(
		getIncidentsCopyStatus,
		(isCopying || isCopyManuallyInitiated) && !isCopied ? 3000 : null,
	);

	const incidentsJiraWebUrl = `/jira/servicedesk/projects/${projectKey}/section/incidents`;

	return (
		<Stack space="space.250">
			<TransitionHomeHeader
				title={formatMessage(stepMessages.incidentsTitleNonFinal)}
				description={formatMessage(stepMessages.incidentsDescriptionNonFinal)}
			/>
			{/* Not a task but loading banner */}
			{isCopying && !isCopyManuallyInitiated && (
				<TransitionHomeContentBox
					illustration={<Image width="262px" src={srcMovingLight} srcDark={srcMovingDark} alt="" />}
				>
					<Stack space="space.200">
						<Heading as="h2" level="h500">
							{formatMessage(messages.loadingTitleNonFinal)}
						</Heading>
						<Text> {formatMessage(messages.loadingDescriptionNonFinal)}</Text>
					</Stack>
				</TransitionHomeContentBox>
			)}

			{/* Not a task but error banner */}
			{hasCopyOperationError && (
				<TransitionHomeContentBox
					illustration={<Image width="80px" src={srcErrorLight} srcDark={srcErrorDark} alt="" />}
				>
					<Stack space="space.200">
						<Text>{formatMessage(messages.errorDescriptionNonFinal)}</Text>
						<Box>
							<Button href={JIRA_SUPPORT_LINK} target="_blank" appearance="warning">
								{formatMessage(messages.contactAtlassianSupportButtonNonFinal)}
							</Button>
						</Box>
					</Stack>
				</TransitionHomeContentBox>
			)}

			{/* Task One */}
			{isCopied && (
				<TransitionHomeContentBox
					illustration={
						<Image
							width="249px"
							src={TaskOneIllustration}
							srcDark={TaskOneIllustrationDark}
							alt=""
						/>
					}
				>
					<Stack space="space.200">
						<Heading as="h2" level="h500">
							{formatMessage(taskMessages.incidentsTaskOneNonFinal)}
						</Heading>
						<Stack space="space.200">
							<Markdown>{formatMessage(messages.taskOneDescriptionNonFinal)}</Markdown>
							<Box>
								<Button href={incidentsJiraWebUrl} target="_blank">
									{formatMessage(messages.taskOneButtonNonFinal)}
								</Button>
							</Box>
						</Stack>
					</Stack>
				</TransitionHomeContentBox>
			)}

			{/* Task Two  */}
			<TransitionHomeContentBox
				illustration={
					<Image width="293px" src={TaskTwoIllustration} srcDark={TaskTwoIllustrationDark} alt="" />
				}
			>
				<Stack space="space.200">
					<Heading as="h2" level="h500">
						{formatMessage(taskMessages.incidentsTaskTwoNonFinal)}
					</Heading>
					<Stack space="space.200">
						<Text>{formatMessage(messages.taskTwoDescriptionNonFinal)}</Text>
						<PriorityTable />
						<StatusTable />
					</Stack>
				</Stack>
			</TransitionHomeContentBox>

			{/* Task Three  */}
			<TransitionHomeContentBox
				illustration={
					<Image width="118px" src={srcGenericErrorLight} srcDark={srcGenericErrorDark} alt="" />
				}
			>
				<Stack space="space.200">
					<Heading as="h2" level="h500">
						{formatMessage(taskMessages.incidentsTaskThreeNonFinal)}
					</Heading>
					<Markdown
						options={{
							overrides: {
								ul: { component: UnorderedList },
								li: { component: UnorderedListItem },
							},
						}}
					>
						{formatMessage(messages.taskThreeDescriptionNonFinal)}
					</Markdown>
				</Stack>
			</TransitionHomeContentBox>

			{/* Task Four  */}
			{(isEligibleForCopy || (isCopying && isCopyManuallyInitiated)) && (
				<TransitionHomeContentBox
					illustration={<Image width="240px" src={TaskFourIllustration} alt="" />}
				>
					<Stack space="space.200">
						<Heading as="h2" level="h500">
							{formatMessage(taskMessages.incidentsTaskFourNonFinal)}
						</Heading>
						<Stack space="space.200">
							<Markdown>{formatMessage(messages.taskFourDescriptionNonFinal)}</Markdown>
							<Checkbox
								isChecked={isChecked}
								onChange={() => setIsChecked((current) => !current)}
								label={formatMessage(messages.taskFourConfirmationNonFinal)}
								isDisabled={isCopying && isCopyManuallyInitiated}
								isRequired
							/>
							<Box>
								<LoadingButton
									onClick={() => {
										setIsCopyManuallyInitiated(true);
										if (!isCopying) {
											copyIncidents();
										}
									}}
									isLoading={isCopying && isCopyManuallyInitiated}
									isDisabled={!isChecked || (isCopying && isCopyManuallyInitiated)}
								>
									{formatMessage(messages.taskFourCopyButtonNonFinal)}
								</LoadingButton>
							</Box>
						</Stack>
					</Stack>
				</TransitionHomeContentBox>
			)}
			{shouldDisplaySuccessBanner && (
				<TransitionHomeContentBox
					illustration={<Image width="240px" src={TaskFourIllustration} alt="" />}
				>
					<Stack space="space.200">
						<Heading as="h2" level="h500">
							{formatMessage(taskMessages.incidentsTaskFourSuccessNonFinal)}
						</Heading>
						<Markdown options={{ overrides: { a: { component: Anchor } } }}>
							{formatMessage(messages.taskFourDescriptionSuccessNonFinal)}
						</Markdown>
					</Stack>
				</TransitionHomeContentBox>
			)}
		</Stack>
	);
};
